import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DataTable,
  type Column,
} from '@themes/aegix';
import { HiArrowLeft } from 'react-icons/hi2';
import { usePlanTemplates } from '../../hooks/queries/useReunification';
import { LabelTemplatesSection } from '../reunification/LabelTemplatesSection';

interface LabelsSignageProps {
  onBack: () => void;
}

interface TemplateRow {
  id: string;
  name: string;
  scale: string;
  incident_type: string;
  is_published: boolean;
}

export function LabelsSignage({ onBack }: LabelsSignageProps) {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState('');

  const { data, isLoading, error } = usePlanTemplates({
    page_size: 200,
  });

  const templates = data?.results ?? [];

  const columns: Column<TemplateRow>[] = useMemo(
    () => [
      { key: 'name', label: t('reunification.planTemplate', 'Plan Template'), sortable: true },
      { key: 'incident_type', label: t('events.incidentType', 'Incident Type'), sortable: true },
      { key: 'scale', label: t('reunification.scale', 'Scale'), sortable: true },
      {
        key: 'is_published',
        label: t('documents.published', 'Published'),
        render: (row) => (row.is_published ? t('common.yes', 'Yes') : t('common.draft', 'Draft')),
      },
    ],
    [t],
  );

  const rows: TemplateRow[] = templates.map((t) => ({
    id: t.public_id,
    name: t.name,
    scale: t.scale_display ?? t.scale,
    incident_type: t.incident_type_display ?? t.incident_type,
    is_published: t.is_published,
  }));

  if (selectedTemplate) {
    return (
      <Box px={{ base: 4, md: 8 }} py={6}>
        <Flex align="center" gap={3} mb={6}>
          <ButtonComponent
            visual="secondary"
            size="sm"
            onClick={() => setSelectedTemplate(null)}
           data-e2e="back-button">
            <HiArrowLeft />
            {t('common.back', 'Back')}
          </ButtonComponent>
          <Box>
            <Heading
              as="h2"
              fontSize="lg"
              fontWeight={600}
              color="text.primary"
            >
              {t('documents.labelsAndSignage', 'Labels & Signage')}
            </Heading>
            <Text fontSize="sm" color="text.secondary">
              {selectedName}
            </Text>
          </Box>
        </Flex>

        <LabelTemplatesSection templateId={selectedTemplate} />
      </Box>
    );
  }

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Flex align="center" gap={3} mb={6}>
        <ButtonComponent visual="secondary" size="sm" onClick={onBack} data-e2e="back-button">
          <HiArrowLeft />
          {t('common.back', 'Back')}
        </ButtonComponent>
        <Box>
          <Heading as="h2" fontSize="lg" fontWeight={600} color="text.primary">
            {t('documents.labelsAndSignage', 'Labels & Signage')}
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {t('documents.selectPlanTemplateInstruction', 'Select a plan template to manage its label and signage templates.')}
          </Text>
        </Box>
      </Flex>

      {isLoading && (
        <Center py={12}>
          <Spinner size="lg" color="button.primary" />
        </Center>
      )}

      {error && (
        <Center py={12}>
          <Text color="text.danger">
            {error instanceof Error
              ? error.message
              : t('documents.failedLoadPlanTemplates', 'Failed to load plan templates')}
          </Text>
        </Center>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <Center py={12}>
          <Text color="text.secondary">{t('documents.noPlanTemplatesFound', 'No plan templates found.')}</Text>
        </Center>
      )}

      {rows.length > 0 && (
        <DataTable
          columns={columns}
          data={rows}
          onRowClick={(row) => {
            setSelectedTemplate(row.id);
            setSelectedName(row.name);
          }}
          keyExtractor={(row) => row.id}
        />
      )}
    </Box>
  );
}
